import { useCallback, useEffect, useMemo, useState } from 'react';
import { differenceInCalendarDays, parseISO, startOfDay } from 'date-fns';
import { useApp } from '@/contexts/AppContext';
import { readScopedLocalJson, writeScopedLocalJson } from '@/lib/offline-core';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { isTransientConnectionError, useNetworkStatus } from '../../ui-foundation/core';
import { GUIDE_SURFACE_META, type GuideSurfaceSection } from '../../ui-foundation/features/guides';
import { getSharedHomeRoutineMeta, type SharedHomeRoutineKind } from '../../ui-foundation';
import { buildSchoolCalendarEventPresentation } from '../../ui-foundation/features/family';
import { buildQuickReminderSubtitle, isQuickReminderEvent } from '../../ui-foundation/features/notifications';

type EventRow = Database['public']['Tables']['events']['Row'];
type RenewalRow = Database['public']['Tables']['renewals']['Row'];
type HealthReminderRow = Database['public']['Tables']['health_reminders']['Row'];
type HmrcDeadlineRow = Database['public']['Tables']['hmrc_deadlines']['Row'];
type DocumentRenewalRow = Database['public']['Tables']['document_renewals']['Row'];
type PlaybookReminderRow = Database['public']['Tables']['playbook_reminders']['Row'];
type HomeRoutineRow = {
  id: string;
  calendar_sync_enabled: boolean;
  routine_kind: SharedHomeRoutineKind;
  title: string;
  due_date: string;
  status: 'active' | 'paused';
};
type FamilySchoolCalendarEventRow = {
  id: string;
  title: string;
  date: string;
  event_kind: string;
  notes: string | null;
  source_label: string;
};

type FamilySchoolCalendarImportRow = {
  selected_school_names: string[] | null;
};

export type CalendarItemSource = 'event' | 'renewal' | 'home-routine' | 'health' | 'hmrc' | 'document' | 'school-import' | 'playbook-reminder';

export interface CalendarItem {
  id: string;
  source: CalendarItemSource;
  sectionLabel: string;
  title: string;
  subtitle: string;
  date: string;
  emoji: string;
  path: string;
  daysUntil: number;
  externalSyncEnabled?: boolean;
}

const EVENT_EMOJI: Record<string, string> = {
  appointment: '🩺',
  school: '🎒',
  bill: '💸',
  birthday: '🎂',
  other: '📌',
};

const HEALTH_EMOJI: Record<string, string> = {
  gp: '🩺',
  dentist: '🦷',
  optician: '👁️',
  smear: '🧪',
  mammogram: '🩺',
  'blood-test': '🩸',
  medication: '💊',
  vaccination: '💉',
  other: '📋',
};

const SOURCE_WEIGHT: Record<CalendarItemSource, number> = {
  hmrc: 0,
  health: 1,
  renewal: 2,
  'home-routine': 3,
  document: 4,
  'school-import': 5,
  'playbook-reminder': 6,
  event: 7,
};

const CALENDAR_CACHE_SCOPE = 'calendar-items';

function isGuideSurfaceSection(value: string): value is GuideSurfaceSection {
  return value in GUIDE_SURFACE_META;
}

function buildDaysUntil(date: string) {
  return differenceInCalendarDays(parseISO(date), startOfDay(new Date()));
}

function sortItems(a: CalendarItem, b: CalendarItem) {
  if (a.daysUntil !== b.daysUntil) return a.daysUntil - b.daysUntil;
  return SOURCE_WEIGHT[a.source] - SOURCE_WEIGHT[b.source];
}

export function useCalendarItems() {
  const { user, data } = useApp();
  const { markConnectionHealthy, reportConnectionIssue } = useNetworkStatus();
  const [items, setItems] = useState<CalendarItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCalendarItems = useCallback(async () => {
    if (!user) {
      setItems([]);
      markConnectionHealthy();
      setLoading(false);
      return;
    }

    try {
      const [
        eventsRes,
        renewalsRes,
        healthRes,
        hmrcRes,
        documentRenewalsRes,
        homeRoutinesRes,
        playbookRemindersRes,
        schoolCalendarRes,
        schoolCalendarImportRes,
      ] = await Promise.all([
        supabase.from('events').select('*').eq('user_id', user.id),
        supabase.from('renewals').select('*').eq('user_id', user.id),
        supabase
          .from('health_reminders')
          .select('*')
          .eq('user_id', user.id)
          .is('completed_at', null),
        supabase
          .from('hmrc_deadlines')
          .select('*')
          .eq('user_id', user.id)
          .eq('completed', false),
        supabase.from('document_renewals').select('*').eq('user_id', user.id),
        (supabase as any)
          .from('home_recurring_routines')
          .select('id, routine_kind, title, due_date, status, calendar_sync_enabled')
          .eq('user_id', user.id)
          .eq('status', 'active'),
        supabase.from('playbook_reminders').select('*').eq('user_id', user.id),
        (supabase as any)
          .from('family_school_calendar_events')
          .select('*')
          .eq('user_id', user.id),
        (supabase as any)
          .from('family_school_calendar_imports')
          .select('selected_school_names')
          .eq('user_id', user.id)
          .maybeSingle(),
      ]);

      const schoolImportRow = (schoolCalendarImportRes as any).data as FamilySchoolCalendarImportRow | null;
      const selectedSchoolNames = Array.isArray(schoolImportRow?.selected_school_names)
        ? schoolImportRow.selected_school_names.filter(Boolean)
        : [];

      const merged: CalendarItem[] = [
        ...((eventsRes.data || []) as EventRow[]).map((event) => ({
          id: `event-${event.id}`,
          source: 'event' as const,
          sectionLabel: isQuickReminderEvent(event as any) ? 'Calendar' : 'Family',
          title: event.title,
          subtitle: isQuickReminderEvent(event as any)
            ? buildQuickReminderSubtitle({
                title: event.title,
                date: event.date,
                time: event.time,
                capture_kind: (event as any).capture_kind,
                reminder_strength: (event as any).reminder_strength,
              })
            : event.time ? `Family event · ${event.time.slice(0, 5)}` : 'Family event',
          date: event.date,
          emoji: isQuickReminderEvent(event as any) ? '⏰' : EVENT_EMOJI[event.type] || '📌',
          path: isQuickReminderEvent(event as any) ? '/calendar' : '/family',
          daysUntil: buildDaysUntil(event.date),
        })),
        ...((renewalsRes.data || []) as RenewalRow[]).map((renewal) => ({
          id: `renewal-${renewal.id}`,
          source: 'renewal' as const,
          sectionLabel: 'Home',
          title: renewal.provider,
          subtitle: 'Renewal due',
          date: renewal.renewal_date,
          emoji: '🏠',
          path: '/home',
          daysUntil: buildDaysUntil(renewal.renewal_date),
        })),
        ...((((homeRoutinesRes as any).data || []) as HomeRoutineRow[]).map((routine) => {
          const meta = getSharedHomeRoutineMeta(routine.routine_kind);
          return {
            id: `home-routine-${routine.id}`,
            source: 'home-routine' as const,
            sectionLabel: 'Home',
            title: routine.title,
            subtitle: meta.subtitle,
            date: routine.due_date,
            emoji: meta.emoji,
            path: '/home?tab=my-home',
            daysUntil: buildDaysUntil(routine.due_date),
            externalSyncEnabled: routine.calendar_sync_enabled,
          };
        })),
        ...((healthRes.data || []) as HealthReminderRow[])
          .filter((reminder) => Boolean(reminder.due_date))
          .map((reminder) => ({
            id: `health-${reminder.id}`,
            source: 'health' as const,
            sectionLabel: 'Health',
            title: reminder.title,
            subtitle: 'Health reminder',
            date: reminder.due_date as string,
            emoji: HEALTH_EMOJI[reminder.type] || '🩺',
            path: '/health',
            daysUntil: buildDaysUntil(reminder.due_date as string),
          })),
        ...((hmrcRes.data || []) as HmrcDeadlineRow[]).map((deadline) => ({
          id: `hmrc-${deadline.id}`,
          source: 'hmrc' as const,
          sectionLabel: 'Work',
          title: deadline.title,
          subtitle: 'HMRC deadline',
          date: deadline.deadline_date,
          emoji: '💼',
          path: '/work',
          daysUntil: buildDaysUntil(deadline.deadline_date),
        })),
        ...((documentRenewalsRes.data || []) as DocumentRenewalRow[]).map((document) => ({
          id: `document-${document.id}`,
          source: 'document' as const,
          sectionLabel: 'Documents',
          title: document.name,
          subtitle: 'Document renewal',
          date: document.date,
          emoji: '🪪',
          path: '/documents',
          daysUntil: buildDaysUntil(document.date),
        })),
        ...((playbookRemindersRes.data || []) as PlaybookReminderRow[])
          .filter((reminder) => isGuideSurfaceSection(reminder.section))
          .map((reminder) => ({
            id: `playbook-reminder-${reminder.id}`,
            source: 'playbook-reminder' as const,
            sectionLabel: GUIDE_SURFACE_META[reminder.section].label,
            title: reminder.title,
            subtitle: reminder.notes || 'Playbook follow-through',
            date: reminder.due_date,
            emoji: '📖',
            path: `/guides/${reminder.section}/playbook/${reminder.playbook_id}`,
            daysUntil: buildDaysUntil(reminder.due_date),
          })),
        ...((((schoolCalendarRes as any).data || []) as FamilySchoolCalendarEventRow[]).map((event) => {
          const presentation = buildSchoolCalendarEventPresentation({
            event: {
              id: event.id,
              title: event.title,
              date: event.date,
              notes: event.notes,
              source_label: event.source_label,
            },
            children: data.profile.children,
            selectedSchoolNames,
          });

          return {
            id: `school-import-${event.id}`,
            source: 'school-import' as const,
            sectionLabel: 'Family',
            title: presentation.title,
            subtitle: presentation.subtitle,
            date: event.date,
            emoji: event.event_kind === 'inset-day' || event.event_kind === 'inset-check' ? '📚' : '🎒',
            path: '/family?tab=calendar',
            daysUntil: buildDaysUntil(event.date),
          };
        })),
      ];

      merged.sort(sortItems);
      setItems(merged);
      writeScopedLocalJson(CALENDAR_CACHE_SCOPE, user.id, merged);
      markConnectionHealthy();
    } catch (error) {
      if (isTransientConnectionError(error)) {
        const cached = readScopedLocalJson<CalendarItem[]>(CALENDAR_CACHE_SCOPE, user.id);
        if (cached?.length) {
          setItems(cached);
        }
        reportConnectionIssue(
          error,
          'Adulting could not refresh your calendar because the connection dropped. Your last loaded details are still on screen.',
        );
      } else {
        console.error('Could not refresh calendar items', error);
      }
    } finally {
      setLoading(false);
    }
  }, [data.profile.children, markConnectionHealthy, reportConnectionIssue, user]);

  useEffect(() => {
    fetchCalendarItems();
  }, [fetchCalendarItems]);

  useEffect(() => {
    const refresh = () => {
      fetchCalendarItems();
    };

    window.addEventListener('focus', refresh);
    window.addEventListener('adulting:data-changed', refresh);

    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('adulting:data-changed', refresh);
    };
  }, [fetchCalendarItems]);

  const overdueItems = useMemo(
    () => items.filter((item) => item.daysUntil < 0),
    [items]
  );

  const upcomingItems = useMemo(
    () => items.filter((item) => item.daysUntil >= 0),
    [items]
  );

  const nextSevenDaysCount = useMemo(
    () => upcomingItems.filter((item) => item.daysUntil <= 7).length,
    [upcomingItems]
  );

  return {
    items,
    loading,
    overdueItems,
    upcomingItems,
    nextSevenDaysCount,
    refreshCalendarItems: fetchCalendarItems,
  };
}
